/**
 * useSprints Hook
 * AIDLC Administrative Backend
 *
 * Provides sprint data for a project with loading/error states.
 * Supports polling interval for background refresh.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchSprints } from '../services/sprintService';
import type { Sprint } from '../types/projects';

const DEFAULT_POLL_INTERVAL = 30000;

interface UseSprintsResult {
  sprints: Sprint[];
  activeSprint: Sprint | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useSprints(projectId: string | null, pollInterval = DEFAULT_POLL_INTERVAL): UseSprintsResult {
  const [sprints, setSprints] = useState<Sprint[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadSprints = useCallback(async () => {
    if (!projectId) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchSprints(projectId);
      setSprints(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch sprints';
      setError(message);
      console.error('[useSprints] Fetch failed:', err);
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (!projectId) {
      setSprints([]);
      return;
    }

    loadSprints();

    if (pollInterval > 0) {
      intervalRef.current = setInterval(loadSprints, pollInterval);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [loadSprints, pollInterval, projectId]);

  const activeSprint = sprints.find((s) => s.status === 'active') ?? null;

  return { sprints, activeSprint, isLoading, error, refresh: loadSprints };
}
